import { c as _c } from "react/compiler-runtime";
import { PortableText } from "@portabletext/react";
import { DKAgreement, DKPaymentMethod, Distribution, TaxUnit } from "../../../../../models";
import { GenericList } from "../GenericList";
import { ListRow } from "../GenericListRow";
import { thousandize } from "../../../../../util/formatting";
import { AgreementDetailsConfiguration } from "./AgreementDetails";
import { DKAgreementDetails } from "./DKAgreementDetails";
import { distributionTargetsMembershipFee } from "./dkMembershipDisplay";
import { CauseAreaContext } from "../../../../shared/components/Widget/types/WidgetProps";
const PAYMENT_METHOD_LABELS: Record<DKPaymentMethod, string> = {
  MobilePay: "MobilePay",
  "Credit card": "Kort",
  "Bank transfer": "Bank"
};
type DKAgreementRow = {
  id: string;
  status: string;
  KID: string;
  date: number;
  amount: number;
  type: DKPaymentMethod;
};
type DKAgreementListConfigurationColumn = {
  title: string;
  value: keyof DKAgreementRow;
  type: "string" | "sum" | "date" | "paymentmethod";
  width?: string;
  payment_date_format_template?: string;
  payment_date_last_day_of_month_template?: string;
  payment_date_membership_format_template?: string;
  hide_on_mobile?: boolean;
};
type DKAgreementListConfiguration = {
  title: string;
  subtitle_text: string;
  list_empty_content: any[];
  columns: DKAgreementListConfigurationColumn[];
  details_configuration?: AgreementDetailsConfiguration;
};
export const DKAgreementList: React.FC<{
  agreements: DKAgreement[];
  distributions?: Map<string, Distribution>;
  taxUnits?: TaxUnit[];
  expandable?: boolean;
  configuration: DKAgreementListConfiguration;
  causeAreaContexts: CauseAreaContext[];
}> = t0 => {
  const $ = _c(19);
  const {
    agreements,
    distributions,
    taxUnits,
    expandable,
    configuration,
    causeAreaContexts
  } = t0;
  let headers;
  let t1;
  if ($[0] !== agreements || $[1] !== causeAreaContexts || $[2] !== configuration.columns || $[3] !== configuration.details_configuration || $[4] !== distributions || $[5] !== expandable || $[6] !== taxUnits) {
    const columns = configuration.columns.filter(_temp);
    headers = columns.map(_temp2);
    t1 = agreements.map(dkAgreement => {
      const row = {
        id: dkAgreement.id,
        status: dkAgreement.cancelled ? "STOPPED" : "ACTIVE",
        KID: dkAgreement.bank_msg ?? dkAgreement.kid,
        date: dkAgreement.chargeDay ?? 0,
        amount: dkAgreement.amount,
        type: dkAgreement.method
      };
      const rowDistribution = distributions?.get(dkAgreement.kid);
      return {
        id: row.id,
        defaultExpanded: false,
        cells: columns.map(column_1 => ({
          value: formatColumnValue(column_1, row[column_1.value], row.type, rowDistribution, dkAgreement.is_payment_expired)
        })),
        details: expandable && configuration.details_configuration && taxUnits ? <DKAgreementDetails agreement={dkAgreement} agreementId={dkAgreement.id} method={dkAgreement.method} inputDistribution={distributions?.get(dkAgreement.kid)} taxUnits={taxUnits} inputSum={row.amount} inputDate={row.date} configuration={configuration.details_configuration} causeAreaContexts={causeAreaContexts} /> : undefined,
        element: row
      };
    });
    $[0] = agreements;
    $[1] = causeAreaContexts;
    $[2] = configuration.columns;
    $[3] = configuration.details_configuration;
    $[4] = distributions;
    $[5] = expandable;
    $[6] = taxUnits;
    $[7] = headers;
    $[8] = t1;
  } else {
    headers = $[7];
    t1 = $[8];
  }
  const rows = t1;
  let t2;
  if ($[9] !== configuration.list_empty_content) {
    t2 = <div data-cy="dk-agreement-list-empty-placeholder"><PortableText value={configuration.list_empty_content} /></div>;
    $[9] = configuration.list_empty_content;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const emptyPlaceholder = t2;
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [20, 70];
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== configuration.subtitle_text || $[13] !== configuration.title || $[14] !== emptyPlaceholder || $[15] !== expandable || $[16] !== headers || $[17] !== rows) {
    t4 = <GenericList emptyPlaceholder={emptyPlaceholder} title={configuration.title} supplementalInformation={configuration.subtitle_text} headers={headers} rows={rows} expandable={expandable} proportions={t3} />;
    $[12] = configuration.subtitle_text;
    $[13] = configuration.title;
    $[14] = emptyPlaceholder;
    $[15] = expandable;
    $[16] = headers;
    $[17] = rows;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
};
const formatColumnValue = (column: DKAgreementListConfigurationColumn, value: any, method: DKPaymentMethod, distribution: Distribution | undefined, isPaymentExpired: boolean) => {
  switch (column.type) {
    case "string":
      return value;
    case "sum":
      return thousandize(Math.round(parseFloat(value))) + " kr";
    case "date":
      if (isPaymentExpired) {
        return "Betalingsmiddel er udløbet";
      }
      if (value === 0) {
        return "Ukendt";
      }
      const membershipTemplate = column.payment_date_membership_format_template?.trim();
      const useMembershipTemplate = distribution && distributionTargetsMembershipFee(distribution) && membershipTemplate;
      const template = useMembershipTemplate ? membershipTemplate : column.payment_date_format_template;
      return template?.replaceAll("{{date}}", String(value));
    case "paymentmethod":
      return PAYMENT_METHOD_LABELS[value as DKPaymentMethod] || value;
  }
};
function _temp(column) {
  return window && !(window.innerWidth < 1180 && column.hide_on_mobile);
}
function _temp2(column_0) {
  return {
    label: column_0.title,
    width: column_0.width
  };
}